import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Send, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatThread = ({ task, currentUser, onMessageSent }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    markAsRead();
  }, [task.id]);

  const markAsRead = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.patch(`${API}/messages/mark-read/${task.id}`, {}, config);
      if (onMessageSent) onMessageSent(); // Trigger parent refresh
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const response = await axios.get(`${API}/messages?task_id=${task.id}`, config);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      
      // Determine recipient
      const toUserId = currentUser.role === 'admin' 
        ? task.assigned_to  // Admin sends to field staff
        : task.created_by;  // Field staff sends to admin

      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };

      const messageData = {
        task_id: task.id,
        to_user_id: toUserId,
        message: newMessage.trim()
      };

      const response = await axios.post(`${API}/messages`, messageData, config);
      setMessages([...messages, response.data]);
      setNewMessage('');
      toast.success('Message sent');
      if (onMessageSent) onMessageSent();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Loading messages...</div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Task Header */}
      <div className="bg-slate-50 p-4 border-b">
        <h3 className="font-bold text-slate-900">{task.title}</h3>
        <p className="text-sm text-slate-600">
          {currentUser.role === 'admin' 
            ? `Chat with ${task.assigned_to_name || 'Field Staff'}`
            : 'Chat with Admin'}
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '400px' }}>
        {messages.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => {
            const isCurrentUser = msg.from_user_id === currentUser.id;
            return (
              <div
                key={msg.id}
                className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] ${isCurrentUser ? 'order-2' : 'order-1'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-6 h-6 rounded-full ${isCurrentUser ? 'bg-teal-600' : 'bg-slate-400'} flex items-center justify-center`}>
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-xs font-medium text-slate-700">
                      {isCurrentUser ? 'You' : msg.from_user_name}
                    </span>
                    <span className="text-xs text-slate-400">
                      {new Date(msg.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                  <div
                    className={`rounded-lg p-3 ${
                      isCurrentUser
                        ? 'bg-teal-600 text-white'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t p-4 bg-white">
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message... (Press Enter to send)"
            rows={2}
            className="flex-1 resize-none"
            data-testid="chat-message-input"
            disabled={sending}
          />
          <Button
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
            className="bg-teal-600 hover:bg-teal-700 self-end"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default ChatThread;
